import { useMemo, useState } from 'react';
import DemoBadge from '@/components/feature/DemoBadge';
import {
  MULTILINGUAL_STORIES,
  TRANSLATION_STAGES,
  WORKFLOW_STAGES,
  languageLabel,
  regionLabelFromId,
} from '@/lib/multilingual';
import type { LangCode, LangVersion, TranslationStage } from '@/lib/multilingual';
import StatusBadge from './StatusBadge';
import type { BadgeTone } from './StatusBadge';

type Draft = { headline: string; summary: string };

function toneForStage(stage: TranslationStage): BadgeTone {
  if (stage === 'Published') return 'live';
  if (stage === 'Approved') return 'done';
  if (stage === 'In review') return 'review';
  if (stage === 'Draft') return 'scheduled';
  return 'draft';
}

export default function MultilingualPublishing() {
  const [selectedId, setSelectedId] = useState(MULTILINGUAL_STORIES[0].id);
  const [stages, setStages] = useState<Record<string, Partial<Record<LangCode, TranslationStage>>>>({});
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [flash, setFlash] = useState('');

  const story = useMemo(
    () => MULTILINGUAL_STORIES.find((s) => s.id === selectedId) ?? MULTILINGUAL_STORIES[0],
    [selectedId],
  );

  const stageFor = (lang: LangCode): TranslationStage => stages[story.id]?.[lang] ?? story.stages[lang] ?? 'Not started';
  const targetLang: LangCode = story.originalLang === 'en' ? 'es' : 'en';

  const esVersion: LangVersion | undefined = story.versions.es;
  const draft: Draft = drafts[story.id] ?? { headline: esVersion?.headline ?? '', summary: esVersion?.summary ?? '' };

  const note = (m: string) => {
    setFlash(m);
    setTimeout(() => setFlash(''), 4000);
  };

  const setStage = (lang: LangCode, stage: TranslationStage) => {
    setStages((prev) => ({ ...prev, [story.id]: { ...prev[story.id], [lang]: stage } }));
    if (stage === 'Published') note('Simulation only — nothing was published. The English version stays live regardless.');
    else if (stage === 'Approved') note('Translation approved in this preview only — no records changed.');
    else if (stage === 'In review') note('Sent for editorial review in this preview only.');
    else note('Stage updated in this preview only.');
  };

  const onDraft = (patch: Partial<Draft>) => {
    setDrafts((prev) => ({ ...prev, [story.id]: { ...draft, ...patch } }));
  };

  const en = story.versions.en;
  const enStage = stageFor('en');
  const esStage = stageFor(targetLang);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-heading font-extrabold uppercase text-xl md:text-2xl text-foreground-950">Multilingual publishing</h2>
        <DemoBadge label="Demo — sample translations" />
      </div>

      {/* Workflow strip */}
      <div className="bg-background-50 border border-background-200 rounded-lg p-4">
        <p className="font-label uppercase tracking-widest text-[10px] font-bold text-foreground-500 mb-3">Workflow</p>
        <div className="flex flex-wrap items-center gap-2">
          {WORKFLOW_STAGES.map((s, i) => (
            <span key={s} className="inline-flex items-center gap-2">
              <span className="font-label uppercase tracking-wide text-[9px] font-semibold px-2.5 py-1 rounded-full border bg-primary-100 border-primary-200 text-primary-700 whitespace-nowrap">{s}</span>
              {i < WORKFLOW_STAGES.length - 1 && <span className="w-3 h-3 flex items-center justify-center text-foreground-300"><i className="ri-arrow-right-s-line"></i></span>}
            </span>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
        {/* Queue */}
        <div className="xl:col-span-5">
          <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-background-200">
              <p className="font-heading font-bold text-lg text-foreground-950">Translation queue</p>
            </div>
            <ul className="divide-y divide-background-200">
              {MULTILINGUAL_STORIES.map((s) => {
                const sStage = stages[s.id]?.es ?? s.stages.es ?? 'Not started';
                const on = s.id === story.id;
                return (
                  <li key={s.id}>
                    <button type="button" onClick={() => setSelectedId(s.id)} className={`w-full text-left px-4 py-3.5 cursor-pointer transition-colors ${on ? 'bg-background-100' : 'hover:bg-background-50'}`}>
                      <div className="flex items-start gap-2.5">
                        <span className="w-5 h-5 flex items-center justify-center text-base shrink-0 mt-0.5 text-accent-700"><i className="ri-translate-2"></i></span>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-foreground-950 leading-snug">{s.versions.en?.headline}</p>
                          <p className="text-xs text-foreground-500 mt-0.5 truncate">{s.category} · {regionLabelFromId(s.regionId)}</p>
                          <p className="text-[11px] text-foreground-400 mt-0.5">Original: {languageLabel(s.originalLang)} → {languageLabel('es')}</p>
                        </div>
                        <StatusBadge tone={toneForStage(sStage)}>{sStage}</StatusBadge>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        {/* Detail */}
        <div className="xl:col-span-7 space-y-5">
          {/* Language version summary */}
          <div className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5">
            <p className="font-label uppercase tracking-widest text-[10px] font-bold text-foreground-500 mb-3">Approved language versions</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <VersionRow langCode="en" stage={enStage} onPublish={() => setStage('en', 'Published')} />
              <VersionRow langCode={targetLang} stage={esStage} onPublish={() => setStage(targetLang, 'Published')} />
            </div>
            <p className="mt-3 text-[11px] text-foreground-500 flex items-start gap-1.5">
              <span className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5"><i className="ri-information-line"></i></span>
              {enStage === 'Published' && esStage !== 'Published'
                ? 'This story is live in English while its Spanish translation is still working through review — the two versions publish independently.'
                : 'Each language version can be reviewed and published on its own timeline.'}
            </p>
          </div>

          {/* Side-by-side preview */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-background-50 border border-background-200 rounded-lg p-4">
              <div className="flex items-center justify-between gap-2">
                <p className="font-label uppercase tracking-widest text-[10px] font-bold text-foreground-500">English version</p>
                <StatusBadge tone={toneForStage(enStage)}>{enStage}</StatusBadge>
              </div>
              {en ? (
                <div className="mt-3">
                  <h4 className="font-heading font-bold text-base text-foreground-950 leading-snug">{en.headline}</h4>
                  <p className="mt-2 text-sm text-foreground-600">{en.summary}</p>
                </div>
              ) : <p className="mt-3 text-sm text-foreground-500">Not available.</p>}
            </div>

            <div className="bg-background-50 border border-background-200 rounded-lg p-4">
              <div className="flex items-center justify-between gap-2">
                <p className="font-label uppercase tracking-widest text-[10px] font-bold text-foreground-500">Spanish version</p>
                <StatusBadge tone={toneForStage(esStage)}>{esStage}</StatusBadge>
              </div>
              {esVersion ? (
                <div className="mt-3 space-y-3">
                  <label className="block">
                    <span className="font-label uppercase tracking-widest text-[9px] text-foreground-500">Headline (editable)</span>
                    <input value={draft.headline} onChange={(e) => onDraft({ headline: e.target.value })} className="mt-1 w-full text-sm border border-background-300 rounded-md px-3 py-2 text-foreground-950 focus:outline-none focus:border-primary-400 bg-background-50" />
                  </label>
                  <label className="block">
                    <span className="font-label uppercase tracking-widest text-[9px] text-foreground-500">Summary (editable)</span>
                    <textarea value={draft.summary} onChange={(e) => onDraft({ summary: e.target.value })} rows={3} className="mt-1 w-full text-sm border border-background-300 rounded-md px-3 py-2 text-foreground-950 focus:outline-none focus:border-primary-400 bg-background-50 resize-none" />
                  </label>
                </div>
              ) : (
                <p className="mt-3 text-sm text-foreground-500">Translation not started for this article.</p>
              )}
              {esVersion && (
                <p className="mt-3 text-[11px] text-foreground-500 flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-robot-2-line"></i></span>
                  {story.translator.es ?? '—'}
                </p>
              )}
            </div>
          </div>

          {/* Editorial decision */}
          <div className="bg-background-50 border border-background-200 rounded-lg p-4">
            <p className="font-label uppercase tracking-widest text-[10px] font-bold text-foreground-600 mb-3">Editorial review — translation</p>
            <div className="flex flex-wrap gap-2.5">
              {TRANSLATION_STAGES.filter((s) => s !== 'Not started').map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStage(targetLang, s)}
                  className={`inline-flex items-center gap-2 font-label font-bold uppercase tracking-wider text-[11px] px-3.5 py-2.5 rounded-md whitespace-nowrap cursor-pointer transition-colors ${s === 'Approved' ? 'bg-primary-500 hover:bg-primary-600 text-background-50' : 'border border-background-300 text-foreground-700 hover:border-foreground-700'}`}
                >
                  <span className="w-4 h-4 flex items-center justify-center"><i className={s === 'Published' ? 'ri-flask-line' : s === 'Approved' ? 'ri-check-line' : s === 'In review' ? 'ri-eye-line' : 'ri-edit-line'}></i></span>
                  {s === 'Published' ? 'Simulate publish' : `/ ${s}`}
                </button>
              ))}
            </div>
            <p className="mt-3 text-[11px] text-foreground-500 flex items-center gap-1.5">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-lock-line"></i></span>
              All decisions are illustrative — no translation service runs, nothing is saved, nothing is published.
            </p>
          </div>

          {flash && (
            <p className="bg-secondary-50 border border-secondary-200 text-secondary-900 text-sm rounded-md px-4 py-3 flex items-start gap-2">
              <span className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0"><i className="ri-information-line"></i></span>{flash}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function VersionRow({ langCode, stage, onPublish }: { langCode: LangCode; stage: TranslationStage; onPublish: () => void }) {
  return (
    <div className="border border-background-200 rounded-md p-3.5 flex items-center justify-between gap-3">
      <div className="min-w-0">
        <p className="text-sm font-semibold text-foreground-950">{languageLabel(langCode)}</p>
        <div className="mt-1.5"><StatusBadge tone={toneForStage(stage)}>{stage}</StatusBadge></div>
      </div>
      <button type="button" onClick={onPublish} disabled={stage === 'Published'} className={`font-label uppercase tracking-wide text-[10px] font-semibold px-3 py-2 rounded-md whitespace-nowrap transition-colors ${stage === 'Published' ? 'text-foreground-400 cursor-default' : 'border border-background-300 text-foreground-700 hover:border-foreground-700 cursor-pointer'}`}>
        {stage === 'Published' ? 'Live' : 'Simulate publish'}
      </button>
    </div>
  );
}